import React, { useMemo, useState } from 'react';
import { formatInTimeZone } from 'date-fns-tz';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp, faEye, faMagnifyingGlass, faXmark } from '@fortawesome/free-solid-svg-icons';

// UI Components
import {
  Drawer,
  DrawerContent,
  DrawerTitle,
  DrawerHeader,
  DrawerClose,
} from '@frontend/shadcn/components/ui/drawer';
import { Button } from '@frontend/shadcn/components/ui/button';
import { Input } from '@frontend/shadcn/components/ui/input';
import { Card, CardContent } from '@frontend/shadcn/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@frontend/shadcn/components/ui/avatar';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@frontend/shadcn/components/ui/accordion';

// Typography and Utils
import { H3 } from '@frontend/shadcn/components/typography/h3';
import { P } from '@frontend/shadcn/components/typography/p';
import { cn } from '@frontend/shadcn/lib/utils';

// Custom Components
import { Tag } from '@frontend/app/refresh-components';

// Hooks and Types
import { useGetAllProjectsQuery } from '@frontend/app/hooks';
import { ProgramOnboardingTemplateType } from '@frontend/app/types/globalTypes';
import { GetAllProjectsQuery_projects } from '@frontend/app/queries/types/GetAllProjectsQuery';
import { Separator } from '@frontend/shadcn/components/ui/separator';
import { useToast } from '@frontend/shadcn/hooks/use-toast';
import { useGetCurrentClient } from '@frontend/app/hooks/client/useGetCurrentClient';
import { isEmpty } from 'lodash';
import { Modal } from '@revfluence/fresh';
import { PROJECT_BY_ID_QUERY } from '@frontend/app/queries';
import { AmplifyIcon, PulseIcon } from './TemplateIcons';
import { useDuplicateProgramOnboardingTemplate } from '../hooks/useDuplicateProgramOnboardingTemplate';
import { useAuth } from '@/context/authContext';

interface CampaignCardProps {
  project: GetAllProjectsQuery_projects;
  isExpanded: boolean;
  onClickDuplicateApplication: (templateId: number, legacyTemplateProgramId?: number) => void;
}

interface DuplicateApplicationDrawerProps {
  projectId: number;
  isOpen: boolean;
  onClose: () => void;
}

function formatDateRange(startDate: string | null | undefined, endDate: string | null | undefined): string {
  if (!startDate) return '';

  const formattedStart = formatInTimeZone(new Date(startDate), 'UTC', 'MMM d, yyyy');
  const formattedEnd = endDate
    ? formatInTimeZone(new Date(endDate), 'UTC', 'MMM d, yyyy')
    : 'Now';

  return `${formattedStart} - ${formattedEnd}`;
}

const CampaignCard: React.FC<CampaignCardProps> = ({ project, isExpanded, onClickDuplicateApplication }) => {
  const [expanded, setExpanded] = useState(isExpanded);

  const formattedDate = useMemo(() =>
    formatDateRange(project.programMetadata?.startDate, project.programMetadata?.endDate),
    [project.programMetadata?.startDate, project.programMetadata?.endDate]
  );

  const applicationPage = useMemo(() => project?.onboardingTemplates || [], [project]);

  const legacyApplicationPage = project?.onboardingTemplateConfig;

  // Get client info for preview URL
  const { client } = useGetCurrentClient();
  const { clientInfo } = useAuth();

  function getPreviewUrl(pageUrl: string | null | undefined): string {
    if (!pageUrl) return '';
    const encodedPageUrl = encodeURIComponent(pageUrl);
    let baseUrl = '';
    if (typeof window !== 'undefined') {
      baseUrl = `${window.location.origin}/join/`;
      if (client?.hostname) {
        baseUrl = baseUrl.replace('community', client.hostname);
      }
    }
    const url = `${baseUrl}${encodedPageUrl}`;
    if (client?.hostname) {
      return url;
    }
    return `${url}?clientId=${clientInfo?.id}`;
  }

  return (
    <Card className="p-3">
      <CardContent className="p-0">
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-3">
            <Avatar className="h-11 w-11 rounded-lg">
              <AvatarImage src={project.splashImageUrl} alt={project.title} className="object-cover rounded-lg" />
              <AvatarFallback className="rounded-lg bg-grey-2">
                {project.title.substring(0, 2).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <H3 className="text-base font-medium text-grey-6 m-0">{project.title}</H3>
          </div>
          <div className="flex items-center justify-end gap-3">
            {formattedDate && <Tag label={formattedDate} bgColor="bg-grey-2" textColor="text-grey-6" />}
            <button onClick={() => setExpanded(!expanded)} className="p-1 hover:bg-grey-1 rounded-full">
              <FontAwesomeIcon icon={expanded ? faChevronUp : faChevronDown} className="h-4 w-4 text-grey-4" />
            </button>
          </div>
        </div>
        {expanded && (
          <>
            <Separator className="my-2" />
            <div className="flex flex-col gap-2">
              {applicationPage.map((template) => (
                <div key={template.id} className="flex items-center justify-between w-full">
                  <div className="flex items-center gap-2">
                    {template.templateType === ProgramOnboardingTemplateType.AMPLIFY && (
                      <AmplifyIcon size="sm" rounded="lg" />
                    )}
                    {template.templateType === ProgramOnboardingTemplateType.PULSE && (
                      <PulseIcon size="sm" rounded="lg" />
                    )}
                    <P className="text-sm font-medium text-grey-6 m-0">{template.applicationTitle}</P>
                  </div>
                  <div className="flex items-center justify-end gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex items-center justify-center px-2 py-1"
                      onClick={() => {
                        const previewUrl = getPreviewUrl(template.pageUrl);
                        if (previewUrl) window.open(previewUrl, '_blank');
                      }}
                    >
                      <FontAwesomeIcon icon={faEye} />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onClickDuplicateApplication(template.id)}
                    >
                      Duplicate
                    </Button>
                  </div>
                </div>
              ))}
              {!isEmpty(legacyApplicationPage) && (
                <div className="flex items-center justify-between w-full">
                  <div className="flex items-center gap-2">
                    {project.applicationPageTemplateName !== 'Quick' && (
                      <AmplifyIcon size="sm" rounded="lg" />
                    )}
                    {project.applicationPageTemplateName === 'Quick' && (
                      <PulseIcon size="sm" rounded="lg" />
                    )}
                    <P className="text-sm font-medium text-grey-6 m-0">{`Legacy ${project.applicationPageTemplateName} Application Page`}</P>
                  </div>
                  <div className="flex items-center justify-end gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex items-center justify-center px-2 py-1"
                      onClick={() => {
                        const previewUrl = getPreviewUrl(legacyApplicationPage?.settings?.url);
                        if (previewUrl) window.open(previewUrl, '_blank');
                      }}
                    >
                      <FontAwesomeIcon icon={faEye} />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onClickDuplicateApplication(0, project.id)}
                    >
                      Duplicate
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export const DuplicateApplicationDrawer: React.FC<DuplicateApplicationDrawerProps> = ({
  projectId,
  isOpen,
  onClose,
}) => {
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const { data: projectsData } = useGetAllProjectsQuery();
  const [duplicateProgramOnboardingTemplate] = useDuplicateProgramOnboardingTemplate();

  const activeProjects = useMemo(
    () =>
      projectsData?.projects
        .filter((project) => project.status === 'active')
        .filter((project) => project.title.toLowerCase().includes(searchQuery.toLowerCase()))
        .sort((a, b) => a.title.localeCompare(b.title)),
    [projectsData, searchQuery],
  );

  const archivedProjects = useMemo(
    () =>
      projectsData?.projects
        .filter((project) => project.status === 'archived')
        .filter((project) => project.title.toLowerCase().includes(searchQuery.toLowerCase()))
        .sort((a, b) => a.title.localeCompare(b.title)),
    [projectsData, searchQuery],
  );

  const onClickDuplicateApplication = async (templateId: number, legacyTemplateProgramId?: number) => {
    try {
      const onConfirm = async () => {
        await duplicateProgramOnboardingTemplate({
          variables: {
            projectId,
            templateId,
            legacyTemplateProgramId,
          },
          refetchQueries: [PROJECT_BY_ID_QUERY]
        });
        onClose();
        toast({
          variant: 'success',
          title: 'Application duplicated successfully',
          duration: 1000,
          className: cn('top-0 right-0 flex fixed md:max-w-[420px] md:top-4 md:right-4'),
        });
      };
      if (legacyTemplateProgramId) {
        Modal.confirm({
          title: 'Duplicate Application',
          content: 'Please note that the styling may be adjusted to match the new page builder when duplicated. We highly recommend that you review all pages before publishing to the marketplace or sending project invitations.',
          onOk: onConfirm,
          okText: 'Duplicate',
          zIndex: 9999999,
          className: 'z-[9999999]',
          wrapClassName: 'z-[9999999]',
        });
      } else {
        await onConfirm();
      }
    } catch (error) {
      toast({
        variant: 'error',
        title: 'Error duplicating application',
        duration: 1000,
        className: cn('top-0 right-0 flex fixed md:max-w-[420px] md:top-4 md:right-4'),
      });
    }
  };

  return (
    <Drawer open={isOpen} direction="right" onOpenChange={onClose}>
      <DrawerContent
        className={cn(
          'fixed left-auto inset-y-0 mt-0 right-0 z-[9999] flex h-full w-[576px] flex-col border-l bg-background rounded-none p-0',
          'duration-300 ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right',
          'bg-background',
          'top-[var(--header-height,0px)] h-[calc(100vh-var(--header-height,0px))]',
          'bg-white',
        )}
        data-vaul-no-drag
      >
        <DrawerHeader className="flex justify-between items-center border-b border-grey-3 px-6 py-4">
          <DrawerTitle className="font-medium mb-0">Duplicate an Application</DrawerTitle>
          <DrawerClose asChild>
            <Button
              variant="ghost"
              size="headerIcon"
              className="border border-transparent hover:border-grey-6 hover:bg-transparent"
            >
              <FontAwesomeIcon icon={faXmark} />
            </Button>
          </DrawerClose>
        </DrawerHeader>

        <div className="flex flex-col gap-4 p-6 overflow-y-auto">
          <div className="relative">
            <FontAwesomeIcon
              icon={faMagnifyingGlass}
              className="absolute left-3 top-1/2 -translate-y-1/2 h-3 w-3 text-grey-6"
            />
            <Input
              placeholder="Search for campaigns"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 h-8 border-grey-2 rounded-lg text-sm"
            />
          </div>

          <Accordion type="multiple" defaultValue={['active', 'archived']} className="w-full [&>*]:border-none">
            <AccordionItem value="active">
              <AccordionTrigger className="text-base font-medium text-primary hover:no-underline p-0 [&>*]:no-underline">
                <div className="flex flex-col">
                  <P className="text-base font-medium text-grey-6 m-0">Active Campaigns</P>
                  <P className="text-xs font-normal text-grey-5 m-0">
                    Launch your new application page by duplicating an application from an active campaign.
                  </P>
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <div className="flex flex-col gap-4">
                  {activeProjects?.filter((project) => project.onboardingTemplates?.length > 0 || !isEmpty(project.onboardingTemplateConfig)).map((project, index) => (
                    <CampaignCard
                      key={project.id}
                      project={project}
                      isExpanded={index === 0}
                      onClickDuplicateApplication={onClickDuplicateApplication}
                    />
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="archived">
              <AccordionTrigger className="text-base font-medium text-primary hover:no-underline p-0 [&>*]:no-underline">
                <div className="flex flex-col">
                  <P className="text-base font-medium text-grey-6 m-0">Archived Campaigns</P>
                  <P className="text-xs font-normal text-grey-5 m-0">
                    Launch your new application page by duplicating an application from an archived campaign.
                  </P>
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <div className="flex flex-col gap-4 pt-2">
                  {archivedProjects?.filter((project) => project.onboardingTemplates?.length > 0 || !isEmpty(project.onboardingTemplateConfig)).map((project, index) => (
                    <CampaignCard
                      key={project.id}
                      project={project}
                      isExpanded={index === 0}
                      onClickDuplicateApplication={onClickDuplicateApplication}
                    />
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </DrawerContent>
    </Drawer>
  );
};
